// Long hosted documents — sections, reading progress, and the signing gate.
// Pure: no React, no Supabase, so the editor, the supplier signer and the tests
// all agree on what a section is and on what's blocking a signature.
//
// SECTIONS, NOT PAGES (long-documents brief §2). Web documents scroll; pages
// only exist at print time. The structural unit is a SECTION BREAK block, and
// sections are DERIVED from the block list rather than stored separately —
// which means a document with no breaks is exactly one section and every short
// document that already exists keeps working untouched.
//
// THE GATE (§4) is "every section opened", and deliberately nothing more:
//   - NO scroll-depth gate, NO minimum dwell time, NO timed continue button.
//     All three are trivially defeated (scroll to bottom, wait, click) and are
//     genuinely painful on a phone, so they buy the appearance of diligence at
//     the cost of real usability.
//   - Dwell time IS recorded as evidence — it just never blocks anyone.
// The blocking rule is that the supplier has opened every section and ticked
// every clause, and the submit control always NAMES what's outstanding rather
// than sitting there disabled with no explanation.

import { coerceSchema } from './hostedForm';

// A section a supplier has never opened. Kept as a named constant because it's
// also the shape a resumed session gets for sections added by a new version.
export const UNOPENED = 'unopened';
export const CURRENT = 'current';
export const VISITED = 'visited';

// The implicit first section — everything before the first section break. It
// needs a stable id (progress is stored against section ids) that can never
// collide with a block id, hence the prefix.
export const FIRST_SECTION_ID = 'sec:intro';

/**
 * Derive the ordered sections of a document from its blocks.
 *
 * Everything up to the first `section_break` is the first section; each break
 * then opens a new one and supplies its title. A document with no breaks comes
 * back as a single section, which is what keeps every existing short document
 * working with no migration.
 *
 * @returns {Array<{id, title, blocks, clauseIds, hasSignature, index}>}
 */
export function sectionsOf(schema) {
  const s = coerceSchema(schema);
  const sections = [];
  let current = { id: FIRST_SECTION_ID, title: '', blocks: [] };

  for (const b of s.blocks) {
    if (b.type === 'section_break') {
      sections.push(current);
      current = { id: b.id, title: String(b.title || ''), blocks: [] };
    } else {
      current.blocks.push(b);
    }
  }
  sections.push(current);

  // Drop a leading empty section: a document that opens with a section break
  // (the natural way to author one) would otherwise show a phantom first
  // section with nothing in it.
  const trimmed = sections[0].blocks.length === 0 && sections.length > 1 ? sections.slice(1) : sections;

  return trimmed.map((sec, index) => ({
    ...sec,
    index,
    // Fall back to the first heading in the section, then to a positional name,
    // so the contents list never shows a blank row.
    title: sec.title.trim()
      || String(sec.blocks.find(b => b.type === 'heading')?.text || '').trim()
      || `Section ${index + 1}`,
    clauseIds: sec.blocks.filter(b => b.type === 'clause').map(b => b.id),
    hasSignature: sec.blocks.some(b => b.type === 'signature'),
  }));
}

/** True when the document is long enough to be worth the sectioned reader. */
export const isSectioned = (schema) => sectionsOf(schema).length > 1;

/**
 * The section holding the signature block — the one that stays locked until
 * everything else has been opened. `-1` when the document has no signature
 * block yet (an unpublishable draft).
 */
export const signatureSectionIndex = (sections) => sections.findIndex(s => s.hasSignature);

/**
 * Per-section reading state for the contents list.
 * `visited` is a Set (or array) of section ids the supplier has opened.
 */
export function sectionState(section, currentId, visited) {
  const seen = visited instanceof Set ? visited : new Set(visited || []);
  if (section.id === currentId) return CURRENT;
  return seen.has(section.id) ? VISITED : UNOPENED;
}

/** Sections whose clauses aren't all ticked, with how many are outstanding. */
export function untickedBySection(sections, ticks = {}) {
  return sections
    .map(s => ({ section: s, outstanding: s.clauseIds.filter(id => !ticks[id]).length }))
    .filter(r => r.outstanding > 0);
}

/**
 * Everything standing between the supplier and a signature.
 *
 * The signature section itself is excluded from the "must be opened" set — it's
 * where they end up, and requiring it to have been visited before it unlocks is
 * circular.
 */
export function outstandingToSign(sections, { visited = [], ticks = {} } = {}) {
  const seen = visited instanceof Set ? visited : new Set(visited || []);
  const sigIndex = signatureSectionIndex(sections);

  const unopened = sections.filter((s, i) => i !== sigIndex && !seen.has(s.id));
  const untickedSections = untickedBySection(sections, ticks);
  const untickedClauses = untickedSections.reduce((n, r) => n + r.outstanding, 0);

  return {
    unopened,
    unopenedCount: unopened.length,
    untickedSections,
    untickedClauses,
    // The signature section is unreachable until every other section is opened.
    signatureLocked: unopened.length > 0,
  };
}

/**
 * The one sentence the Sign control shows. The brief is explicit that the
 * control must NAME what's outstanding rather than silently disabling, because
 * a disabled button with no explanation is the single most common way a
 * supplier gets stuck (and it's exactly what happened on the short-form modal
 * before the footer was made to explain itself).
 *
 * Returns null when nothing is outstanding — i.e. they can sign.
 */
export function outstandingMessage(sections, { visited = [], ticks = {} } = {}) {
  const { unopenedCount, untickedClauses } = outstandingToSign(sections, { visited, ticks });
  const parts = [];
  if (unopenedCount > 0) {
    parts.push(`${unopenedCount} section${unopenedCount === 1 ? '' : 's'} not yet opened`);
  }
  if (untickedClauses > 0) {
    parts.push(`${untickedClauses} clause${untickedClauses === 1 ? '' : 's'} still to agree`);
  }
  if (!parts.length) return null;
  return parts.join(' and ');
}

/** Can they sign? (Reading + clauses only — name/signature live in the form.) */
export const readingComplete = (sections, progress) => outstandingMessage(sections, progress) === null;

/** 0–1 progress across sections opened, for the header bar. */
export function readingProgress(sections, visited = []) {
  const seen = visited instanceof Set ? visited : new Set(visited || []);
  if (!sections.length) return 0;
  const done = sections.filter(s => seen.has(s.id)).length;
  return Math.min(1, done / sections.length);
}

/**
 * Where "Save and finish later" should drop them back (§5). Returns the stored
 * section id when it still exists in this version, otherwise the first
 * unopened one, otherwise the start.
 *
 * A section id that has vanished means the document was republished with that
 * section removed — resuming into it would be a blank screen.
 */
export function resumeSectionId(sections, { lastSectionId = null, visited = [] } = {}) {
  if (!sections.length) return null;
  if (lastSectionId && sections.some(s => s.id === lastSectionId)) return lastSectionId;
  const seen = visited instanceof Set ? visited : new Set(visited || []);
  const firstUnopened = sections.find(s => !seen.has(s.id));
  return (firstUnopened || sections[0]).id;
}

/**
 * Carry reading progress across a NEW VERSION of the document.
 *
 * DECISION (brief §9 was left open): any new version resets EVERY section to
 * unopened. The alternative — reopening only the sections that changed — needs
 * a reliable block-level diff, and getting it wrong means a supplier signs a
 * changed clause they were never shown again. Resetting is the conservative
 * failure: it costs them a re-read, it can't cost them an unseen obligation.
 * Worth revisiting once the amendment diff is trusted.
 *
 * Section ids that no longer exist are dropped either way.
 */
export function carryProgressToVersion(sections, progress, { sameVersion = false } = {}) {
  const ids = new Set(sections.map(s => s.id));
  if (!sameVersion) return { visited: [], lastSectionId: null };
  const visited = (progress?.visited || []).filter(id => ids.has(id));
  return {
    visited,
    lastSectionId: ids.has(progress?.lastSectionId) ? progress.lastSectionId : null,
  };
}

/**
 * Evidence of how the document was read — recorded, never enforced (§4).
 * Dwell is summed per section across visits, so flicking back and forth reads
 * as one total rather than resetting.
 */
export function summariseReadingEvidence(sections, events = []) {
  const bySection = new Map();
  for (const ev of events) {
    if (!ev || !ev.sectionId) continue;
    const prev = bySection.get(ev.sectionId) || { sectionId: ev.sectionId, visits: 0, dwellMs: 0, firstAt: null, lastAt: null };
    prev.visits += 1;
    prev.dwellMs += Math.max(0, Number(ev.dwellMs) || 0);
    if (!prev.firstAt || (ev.at && ev.at < prev.firstAt)) prev.firstAt = ev.at || prev.firstAt;
    if (!prev.lastAt || (ev.at && ev.at > prev.lastAt)) prev.lastAt = ev.at || prev.lastAt;
    bySection.set(ev.sectionId, prev);
  }
  return {
    // Ordered by the document, not by when they happened — a reviewer reads it
    // alongside the document.
    sections: sections.map(s => bySection.get(s.id) || { sectionId: s.id, visits: 0, dwellMs: 0, firstAt: null, lastAt: null }),
    totalDwellMs: [...bySection.values()].reduce((n, r) => n + r.dwellMs, 0),
    // The order they FIRST opened each section, which is the interesting one:
    // straight through, or straight to the signature and back?
    openOrder: [...bySection.values()]
      .filter(r => r.firstAt)
      .sort((a, b) => String(a.firstAt).localeCompare(String(b.firstAt)))
      .map(r => r.sectionId),
  };
}
